import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './DashboardHeader.module.scss'
import { MessageSquare, X, SendHorizonal, Plus } from 'lucide-react'
import {
  getDesktopSuggestions,
  getMobileResponses,
  getPostFlowSuggestions,
  normalizeText,
  type DesktopSuggestion,
  type MobileResponse
} from './dashboardActionsConfig.tsx'
import { withConversationState } from '../../../utils/navigationHelpers.ts'
import useNavVisibility from '../../../hooks/useNavVisibility.ts'
import { useDispatch } from 'react-redux'
import { useConversationalAgent } from '../../../contexts/ConversationalAgentContext.tsx'
import ConversationalMode from './ConversationalMode/ConversationalMode.tsx'
import ChatSearchModal from './ConversationalMode/ChatSearchModal/ChatSearchModal.tsx'
import { registerTutorialRef } from '../../../utils/tutorialRefsRegistry.ts'
import { AGENT_NAMES } from '../../../constants/agentNames.ts'

const MOBILE_PRIORITY_IDS = ['cotizar-materiales', 'ofrecer-servicios', 'publicar-oferta-trabajo']
const MOBILE_PRIORITY_ROTATIONS: string[][] = [
  ['cotizar-materiales', 'ofrecer-servicios', 'publicar-oferta-trabajo'],
  ['ofrecer-servicios', 'publicar-oferta-trabajo', 'cotizar-materiales'],
  ['publicar-oferta-trabajo', 'cotizar-materiales', 'ofrecer-servicios'],
]
const SUBTITLE_PHRASES = [
  '¿En qué podemos ayudarte hoy?',
  '¿Querés cotizar materiales?',
  '¿Buscás profesionales o contratistas?',
  '¿Necesitás publicar una vacante?',
  '¿Querés publicar un servicio o producto?',
  '¿Necesitás presupuestos para tu obra?',
  '¿Buscás licitaciones públicas?',
  '¿Querés ver cotizaciones disponibles?',
]

const SESSION_CHIPS_BUCKET_KEY = 'edify_mobile_chips_bucket'

const getSessionChipsBucket = (userId?: string): number => {
  if (typeof window === 'undefined') return 0
  try {
    const key = `${SESSION_CHIPS_BUCKET_KEY}_${userId ?? 'anon'}`
    const stored = sessionStorage.getItem(key)
    if (stored !== null) {
      const n = parseInt(stored, 10)
      if (n >= 0 && n < MOBILE_PRIORITY_ROTATIONS.length) return n
    }
    const bucket = Math.floor(Math.random() * MOBILE_PRIORITY_ROTATIONS.length)
    sessionStorage.setItem(key, String(bucket))
    return bucket
  } catch {
    return 0
  }
}

export interface MobileResultsPayload {
  status: 'idle' | 'loading' | 'result' | 'empty'
  label?: string
  summary?: string
  cta?: () => void
}

interface DashboardHeaderProps {
  userName: string
  userId?: string
  userData?: any
  onMobileResultsChange?: (payload: MobileResultsPayload) => void
  onConversationalModeChange?: (isActive: boolean) => void
  onAgentStatusChange?: (isProcessing: boolean) => void
  onConversationalActionsReady?: (actions: {
    onCreateNewSession: () => void
    onOpenSearchModal: () => void
    onCloseConversationalMode: () => void
    hasActiveConversation?: boolean
    totalChatsCount?: number
  }) => void
  onSidebarStateChange?: (isCollapsed: boolean) => void
  sidebarCollapsed?: boolean
  mobileActionsPlaceholder?: React.ReactNode
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  userName,
  userId,
  userData,
  onMobileResultsChange,
  onConversationalModeChange,
  onAgentStatusChange,
  onConversationalActionsReady,
  onSidebarStateChange,
  sidebarCollapsed = false,
  mobileActionsPlaceholder
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [matchedSuggestion, setMatchedSuggestion] = useState<DesktopSuggestion | null>(null)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [subtitleDisplayText, setSubtitleDisplayText] = useState('')
  const [subtitlePhraseIndex, setSubtitlePhraseIndex] = useState(0)
  const [isDeletingSubtitle, setIsDeletingSubtitle] = useState(false)
  const [isHoveringSubtitle, setIsHoveringSubtitle] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    removeSession,
    clearCurrentSession,
    isConversationalModeActive,
    activateConversationalMode,
    deactivateConversationalMode,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    activeAgent,
    collectedData,
    sendMessage,
    isProcessing: contextIsProcessing,
    missingFields,
    readyToExecute,
    hasPendingImage,
    imageUploadAcknowledged,
    pendingImagePreview,
    pendingAttachmentMime,
    documentPipelineLoopActive,
    documentPipelinePhase,
    documentPipelineStreamHint,
    storePendingImage,
    clearPendingImage,
  } = useConversationalAgent()

  const { ocultarEnMobile } = useNavVisibility()
  const isMobile = ocultarEnMobile
  const historyCount = allSessions.length
  const historyCountLabel = historyCount > 99 ? '99+' : String(historyCount)
  const mobileSuggestionsRef = useRef<HTMLDivElement | null>(null)
  const navigateFromConversation = useMemo(() => withConversationState(navigate), [navigate])
  const desktopSuggestions = useMemo(() => {
    const base = getDesktopSuggestions(navigateFromConversation, userId, userData, dispatch)
    const postFlow = getPostFlowSuggestions(() => sendMessage('', { confirmExecution: true }))
    return [...base, ...postFlow]
  }, [navigateFromConversation, userId, userData, dispatch, sendMessage])
  const mobileResponses = useMemo(() => getMobileResponses(navigateFromConversation, userId, userData, dispatch), [navigateFromConversation, userId, userData, dispatch])
  const mobileQuickSuggestions = useMemo(() => {
    const rotation = MOBILE_PRIORITY_ROTATIONS[getSessionChipsBucket(userId)]
    const prioritized = rotation
      .map(id => mobileResponses.find(suggestion => suggestion.id === id))
      .filter((suggestion): suggestion is MobileResponse => Boolean(suggestion))
    const remaining = mobileResponses.filter(suggestion => !MOBILE_PRIORITY_IDS.includes(suggestion.id))
    return [...prioritized, ...remaining].slice(0, 8)
  }, [mobileResponses, userId])
  useEffect(() => {
    if (!isMobile) {
      setShowMobileSuggestions(false)
      setShowLeftFade(false)
      return
    }

    const timeout = window.setTimeout(() => {
      setShowMobileSuggestions(true)
    }, 800)

    return () => window.clearTimeout(timeout)
  }, [isMobile])

  useEffect(() => {
    if (!showMobileSuggestions) {
      return
    }

    const element = mobileSuggestionsRef.current
    if (!element) {
      return
    }

    const updateFade = () => {
      setShowLeftFade(element.scrollLeft > 4)
    }

    updateFade()
    element.addEventListener('scroll', updateFade, { passive: true })

    return () => {
      element.removeEventListener('scroll', updateFade)
    }
  }, [showMobileSuggestions])

  useEffect(() => {
    if (isFocused || searchQuery || isProcessing) {
      return
    }

    const currentText = SUBTITLE_PHRASES[subtitlePhraseIndex % SUBTITLE_PHRASES.length]
    let timeoutId = 0

    if (!isDeletingSubtitle) {
      if (subtitleDisplayText.length < currentText.length) {
        timeoutId = window.setTimeout(() => {
          setSubtitleDisplayText(currentText.slice(0, subtitleDisplayText.length + 1))
        }, 45)
      } else if (!isHoveringSubtitle) {
        timeoutId = window.setTimeout(() => {
          setIsDeletingSubtitle(true)
        }, 1800)
      }
    } else {
      if (subtitleDisplayText.length > 0) {
        timeoutId = window.setTimeout(() => {
          setSubtitleDisplayText(currentText.slice(0, subtitleDisplayText.length - 1))
        }, 28)
      } else if (isHoveringSubtitle) {
        setSubtitleDisplayText(currentText)
        setIsDeletingSubtitle(false)
      } else {
        setIsDeletingSubtitle(false)
        setSubtitlePhraseIndex((prev) => (prev + 1) % SUBTITLE_PHRASES.length)
      }
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [isFocused, searchQuery, isProcessing, isHoveringSubtitle, subtitlePhraseIndex, subtitleDisplayText, isDeletingSubtitle])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 12) return "Buenos días"
    if (hour >= 12 && hour < 20) return "Buenas tardes"
    return "Buenas noches"
  }

  const normalizedQuery = normalizeText(searchQuery).trim()
  const hasTypedQuery = normalizedQuery.length > 0

  const handleClearSearch = () => {
    setSearchQuery('')
    setMatchedSuggestion(null)
    setIsProcessing(false)
    onAgentStatusChange?.(false)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!hasTypedQuery || isProcessing) {
      return
    }

    const userQuery = searchQuery.trim()
    setIsProcessing(true)
    onAgentStatusChange?.(true)
    if (isMobile) {
      if (showMobileSuggestions) {
        setShowMobileSuggestions(false)
      }
      onMobileResultsChange?.({ status: 'loading' })
    }
    setMatchedSuggestion(null)

    createSession()
    if (!isConversationalModeActive) {
      activateConversationalMode()
    }

    const suggestionPool: (DesktopSuggestion | MobileResponse)[] = isMobile ? mobileResponses : desktopSuggestions
    let suggestionMatch: DesktopSuggestion | MobileResponse | undefined = undefined
    let assistantResponse: string | null = null
    let llmResultConfidence = 0
    let llmResultActionId: string | null = null

    try {
      const llmResult = await sendMessage(userQuery)
      assistantResponse = llmResult?.response || null
      llmResultConfidence = llmResult?.confidence || 0
      llmResultActionId = llmResult?.actionId || null

      if (llmResult?.actionId) {
        const llmMatch = suggestionPool.find(config => config.id === llmResult.actionId)
        if (llmMatch && llmResult.confidence > 0.5) {
          suggestionMatch = llmMatch
        }
      }
    } catch (error) {
      console.warn('Error al consultar LLM:', error)
    }

    if (suggestionMatch) {
      if (llmResultActionId === 'postularme-ofertas' && llmResultConfidence > 0.6) {
        setMatchedSuggestion(suggestionMatch as DesktopSuggestion)
      } else if (llmResultConfidence > 0.7) {
        setMatchedSuggestion(suggestionMatch as DesktopSuggestion)
      } else {
        setMatchedSuggestion((suggestionMatch as DesktopSuggestion) ?? null)
      }
    } else {
      setMatchedSuggestion(null)
    }
    setIsProcessing(false)
    onAgentStatusChange?.(false)
    if (isMobile) {
      if (suggestionMatch) {
        onMobileResultsChange?.({
          status: 'result',
          label: suggestionMatch.label,
          summary: assistantResponse || (suggestionMatch as MobileResponse).summary,
          cta: suggestionMatch.action
        })
      } else {
        onMobileResultsChange?.({ status: 'empty' })
      }
    }

    setSearchQuery('')
  }

  const handleSendMessage = async (message: string, options?: { confirmExecution?: boolean }) => {
    if (!canSendMessage && !options?.confirmExecution) {
      return
    }

    await sendMessage(message, options)
  }

  useEffect(() => {
    onConversationalModeChange?.(isConversationalModeActive)
  }, [isConversationalModeActive, onConversationalModeChange])

  const handleCloseConversationalMode = useCallback(() => {
    deactivateConversationalMode()
    clearCurrentSession()
  }, [deactivateConversationalMode, clearCurrentSession])

  const handleCreateNewSession = useCallback(() => {
    createSession()
    activateConversationalMode()
  }, [createSession, activateConversationalMode])

  const openSearchModalRef = useRef<(() => void) | null>(null)
  const onConversationalActionsReadyRef = useRef(onConversationalActionsReady)

  const handleOpenHistoryModal = useCallback(() => {
    if (isConversationalModeActive && openSearchModalRef.current) {
      openSearchModalRef.current()
    } else {
      setIsHistoryModalOpen(true)
    }
  }, [isConversationalModeActive])

  useEffect(() => {
    onConversationalActionsReadyRef.current = onConversationalActionsReady
  }, [onConversationalActionsReady])

  useEffect(() => {
    if (onConversationalActionsReadyRef.current) {
      const hasActiveConversation = currentSession && currentSession.messages.length > 0
      const actions = {
        onCreateNewSession: handleCreateNewSession,
        onOpenSearchModal: handleOpenHistoryModal,
        onCloseConversationalMode: handleCloseConversationalMode,
        hasActiveConversation: hasActiveConversation,
        totalChatsCount: allSessions.length
      }
      onConversationalActionsReadyRef.current(actions)
    }
  }, [isConversationalModeActive, handleCreateNewSession, handleCloseConversationalMode, handleOpenHistoryModal, currentSession, allSessions.length])

  const handleSuggestionAction = () => {
    if (!matchedSuggestion) {
      return
    }

    matchedSuggestion.action()
    setSearchQuery('')
    setMatchedSuggestion(null)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  const handleSendMessageRef = useRef(handleSendMessage)
  handleSendMessageRef.current = handleSendMessage

  const handleSubtitleClick = useCallback(() => {
    if (isProcessing) return
    const phraseIndex = subtitlePhraseIndex % SUBTITLE_PHRASES.length
    const phrase = phraseIndex === 0 ? '¿Qué puedo hacer en Edify?' : SUBTITLE_PHRASES[phraseIndex]
    if (!isConversationalModeActive) {
      createSession()
      activateConversationalMode()
    }
    if (isMobile) {
      if (showMobileSuggestions) {
        setShowMobileSuggestions(false)
      }
      onMobileResultsChange?.({ status: 'loading' })
    }
    handleSendMessageRef.current(phrase)
  }, [isProcessing, subtitlePhraseIndex, isConversationalModeActive, createSession, activateConversationalMode, isMobile, showMobileSuggestions, onMobileResultsChange])

  if (isConversationalModeActive) {
    return (
      <div className={`${styles.conversationalModeContainer} ${isMobile ? styles.conversationalModeContainerMobile : ''}`}>
        <ConversationalMode
          isActive={isConversationalModeActive}
          isMobile={isMobile}
          messages={currentSession?.messages || []}
          onSendMessage={async (msg, opts) => {
            await sendMessage(msg, opts)
          }}
          onClose={handleCloseConversationalMode}
          isLoading={contextIsProcessing}
          showDocumentPipelineLoop={documentPipelineLoopActive}
          documentPipelinePhase={documentPipelinePhase}
          documentPipelineStreamHint={documentPipelineStreamHint}
          canSendMessage={canSendMessage}
          remainingMessages={remainingMessages}
          hasReachedLimit={hasReachedLimit}
          limit={limit}
          matchedSuggestion={matchedSuggestion}
          onSuggestionAction={handleSuggestionAction}
          onRequestGuide={() => sendMessage('Ayudame a hacerlo')}
          allSessions={allSessions}
          currentSessionId={currentSession?.sessionId || null}
          onSelectSession={(sessionId) => {
            void loadSession(sessionId).then((session) => {
              if (session) {
                activateConversationalMode()
              }
            })
          }}
          onCreateNewSession={() => {
            createSession()
            activateConversationalMode()
          }}
          onDeleteSession={removeSession}
          userId={userId}
          userData={userData}
          searchModalRef={openSearchModalRef}
          onSidebarStateChange={onSidebarStateChange}
          missingFields={missingFields}
          readyToExecute={readyToExecute}
          showImageUpload={
            activeAgent === AGENT_NAMES.projectAutofillAgent ||
            collectedData?.intent === 'project_autofill' ||
            missingFields.includes('image_uploaded') ||
            (readyToExecute && collectedData?.intent === 'create_post')
          }
          allowDocuments={
            activeAgent === AGENT_NAMES.projectAutofillAgent || collectedData?.intent === 'project_autofill'
          }
          collectedData={collectedData}
          hasPendingImage={hasPendingImage}
          imageUploadAcknowledged={imageUploadAcknowledged}
          pendingImagePreview={pendingImagePreview}
          pendingAttachmentMime={pendingAttachmentMime}
          onImageSelect={storePendingImage}
          onImageClear={clearPendingImage}
          onConfirmExecution={() => sendMessage('', { confirmExecution: true })}
        />
      </div>
    )
  }

  return (
    <div className={styles.initialModeContainer}>
      <div className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.greetingSection}>
            <h1 className={styles.greeting}>
              {getGreeting()}, <span className={styles.userName}>{userName}</span>
            </h1>
            <button
              type="button"
              className={`${styles.subtitle} ${styles.subtitleClickable}`}
              onClick={handleSubtitleClick}
              onMouseEnter={() => setIsHoveringSubtitle(true)}
              onMouseLeave={() => setIsHoveringSubtitle(false)}
              disabled={isProcessing}
            >
              {subtitleDisplayText}
            </button>
          </div>
        </div>

      </div>

      {/* {isMobile && mobileActionsPlaceholder} */}
      {isMobile && (
        <div
          className={`${styles.mobileSuggestions} ${showMobileSuggestions ? styles.mobileSuggestionsVisible : ''}`}
        >
          <div
            className={`${styles.mobileSuggestionsViewport} ${showLeftFade ? styles.mobileSuggestionsViewportLeftFade : ''}`}
          >
            <div className={styles.mobileSuggestionList} ref={mobileSuggestionsRef}>
              {mobileQuickSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={styles.mobileSuggestionCard}
                  onClick={() => suggestion.action()}
                >
                  {suggestion.label}
                  <Plus size={16} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className={styles.searchSection}>
        <form className={styles.searchForm} onSubmit={handleSubmit}>
          <div
            ref={(el) => registerTutorialRef('welcome-ai-input', el)}
            data-tutorial="welcome-ai-input"
            className={`${styles.searchBox} ${isFocused ? styles.searchBoxFocused : ''}`}
          >
            <textarea
              className={styles.searchInput}
              placeholder="Contanos qué querés hacer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  event.currentTarget.form?.requestSubmit()
                }
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 200)}
              disabled={isProcessing}
              rows={1}
            />
            <div className={styles.actionButtons}>
              {isMobile && historyCount > 0 && (
                <button
                  type="button"
                  className={styles.historyButton}
                  onClick={() => setIsHistoryModalOpen(true)}
                  aria-label="Ver conversaciones anteriores"
                  title="Historial de conversaciones"
                >
                  <MessageSquare size={18} />
                  <span className={styles.historyBadge} aria-hidden="true">
                    {historyCountLabel}
                  </span>
                </button>
              )}
              {searchQuery && !isProcessing && (
                <button
                  className={styles.clearButton}
                  onClick={handleClearSearch}
                  type="button"
                >
                  <X size={16} />
                </button>
              )}
              <button
                type="submit"
                className={styles.sendIconButton}
                disabled={!hasTypedQuery || isProcessing}
              >
                <SendHorizonal size={18} />
              </button>
            </div>
          </div>
        </form>
      </div>

      <ChatSearchModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        sessions={allSessions}
        currentSessionId={currentSession?.sessionId || null}
        onSelectSession={(sessionId) => {
          setIsHistoryModalOpen(false)
          void loadSession(sessionId).then((session) => {
            if (session) {
              activateConversationalMode()
            }
          })
        }}
        onCreateNewSession={() => {
          setIsHistoryModalOpen(false)
          createSession()
          activateConversationalMode()
        }}
        onDeleteSession={removeSession}
        userId={userId}
        userData={userData}
      />
    </div>
  )
}

export default DashboardHeader

